import React from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  section: {
    maxWidth: '1314px',
    width: '100%',
    margin: '0 auto 70px auto',
    padding: '0 24px',
    boxSizing: 'border-box',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      margin: '0 auto 36px auto',
      padding: '0 20px',
    }
  },
  sectionTitle: {
    fontFamily: '"Cormorant Garamond", serif',
    fontSize: '52px',
    fontWeight: 400,
    lineHeight: '100%',
    color: '#ffffff',
    marginBottom: '48px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '36px',
      marginBottom: '20px',
    }
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
      gap: '20px',
    }
  },
  card: {
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#0C0A09',
    border: '1px solid var(--Middle-gray, #1F1F1F)',
    borderRadius: '16px',
    padding: '32px 28px',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    transition: 'border-color 0.2s ease, transform 0.2s ease',
    '&:hover': {
      borderColor: 'rgba(253, 60, 132, 0.4)',
      transform: 'translateY(-2px)',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '0 20px 24px 20px',
    }
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  iconBadge: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    backgroundColor: '#2A131E',
    border: '1px solid rgba(253, 60, 132, 0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardNumber: {
    fontFamily: '"Cormorant Garamond", serif',
    fontStyle: 'italic',
    fontSize: '64px',
    fontWeight: 400,
    color: '#1F1F1F',
    userSelect: 'none',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '9px',
    }
  },
  cardTitle: {
    fontFamily: '"Cormorant Garamond", serif',
    fontSize: '28px',
    fontWeight: 500,
    lineHeight: '100%',
    color: '#ffffff',
    marginBottom: '12px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '20px',
    }
  },
  cardDesc: {
    fontFamily: '"Roboto", sans-serif',
    fontSize: '18px',
    fontWeight: 200,
    color: 'rgba(255, 255, 255, 0.65)',
    lineHeight: '22px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
      lineHeight: '24px',
    }
  }
}));

const PRINCIPLES = [
  {
    number: "01",
    title: "Exclusivity",
    desc: "We create a curated environment where premium creators and high-value members can connect through meaningful experiences, not mass-market engagement.",
    icon: (
      <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.6125 12.3941H20.3875M9.6125 12.3941H3.4375M9.6125 12.3941L14.175 24.9579M9.6125 12.3941L12.6125 7.47789L13.3 6.21289M20.3875 12.3941H26.5625M20.3875 12.3941L15.825 24.9579M20.3875 12.3941L17.3875 7.47789L16.68 6.21289M26.5625 12.3941C26.5625 12.9191 26.4637 13.4441 26.2675 13.9391C26.0025 14.6079 25.465 15.2241 24.3925 16.4554L20.0588 21.4254C18.3125 23.4279 17.44 24.4304 16.405 24.7979C16.2157 24.8649 16.0219 24.9183 15.825 24.9579C15.2803 25.0657 14.7197 25.0657 14.175 24.9579C13.9792 24.9187 13.7858 24.8654 13.595 24.7979C12.56 24.4304 11.6875 23.4279 9.94125 21.4254L5.6075 16.4554C4.535 15.2241 3.9975 14.6079 3.7325 13.9391C3.53625 13.4441 3.4375 12.9191 3.4375 12.3941M3.4375 12.3941C3.4375 11.8691 3.53625 11.3441 3.7325 10.8479C3.9975 10.1791 4.535 9.56414 5.6075 8.33164C6.19125 7.66414 6.4825 7.33039 6.82 7.07164C7.32177 6.68922 7.90325 6.42476 8.52125 6.29789C8.93875 6.21289 9.38125 6.21289 10.2675 6.21289H13.3M13.3 6.21289H16.68M26.5625 12.3941C26.5625 11.8691 26.4637 11.3441 26.2675 10.8479C26.0025 10.1791 25.465 9.56414 24.3925 8.33164C23.8087 7.66414 23.5175 7.33039 23.18 7.07164C22.6782 6.68922 22.0967 6.42476 21.4788 6.29789C21.0613 6.21289 20.6188 6.21289 19.7325 6.21289H16.68" stroke="#FD3C84" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  },
  {
    number: "02",
    title: "Trust",
    desc: "Privacy, identity verification, moderation, and platform safety are built into every part of the SugarCribs experience, creating confidence for both creators and members.",
    icon: (
      <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M25.3687 15.8509C24.9028 15.8509 24.525 16.2287 24.525 16.6946V20.108C24.525 21.4937 23.3991 22.6205 22.0134 22.6205H14.5509C14.3616 22.6205 14.1788 22.6843 14.0297 22.8015L9.3375 26.5018V23.4652C9.3375 22.9993 8.95969 22.6215 8.49375 22.6215H5.23125C3.84562 22.6215 2.71875 21.4946 2.71875 20.109V9.58086C2.71875 8.19523 3.84562 7.06836 5.23125 7.06836H16.8375C17.3034 7.06836 17.6812 6.69055 17.6812 6.22461C17.6812 5.75867 17.3034 5.38086 16.8375 5.38086H5.23125C2.91562 5.38086 1.03125 7.26523 1.03125 9.58086V20.109C1.03125 22.4255 2.91562 24.309 5.23125 24.309H7.65V26.5018C7.65 27.1468 8.01188 27.7252 8.59312 28.0084C8.83125 28.1246 9.08437 28.1818 9.33562 28.1818C9.70406 28.1818 10.0678 28.0599 10.3734 27.8209L14.8416 24.309H22.0125C24.3281 24.309 26.2116 22.4246 26.2116 20.109V16.6955C26.2125 16.2296 25.8347 15.8509 25.3687 15.8509Z" fill="#FD3C84" />
        <path d="M26.9869 6.10648V4.94586C26.9869 3.2218 25.5834 1.81836 23.8594 1.81836C22.1353 1.81836 20.7319 3.2218 20.7319 4.94586V6.10648C19.6134 6.27242 18.75 7.23055 18.75 8.39398V11.933C18.75 13.2127 19.7916 14.2552 21.0722 14.2552H26.6466C27.9263 14.2552 28.9688 13.2137 28.9688 11.933V8.39398C28.9678 7.23055 28.1044 6.27336 26.9869 6.10648ZM23.8594 3.5068C24.6534 3.5068 25.2984 4.1518 25.2984 4.94586V6.0718H22.4203V4.94586C22.4203 4.15273 23.0653 3.5068 23.8594 3.5068ZM27.2803 11.934C27.2803 12.2837 26.9963 12.5677 26.6466 12.5677H21.0722C20.7225 12.5677 20.4384 12.2837 20.4384 11.934V8.39398C20.4384 8.0443 20.7225 7.76023 21.0722 7.76023H26.6466C26.9963 7.76023 27.2803 8.0443 27.2803 8.39398V11.934Z" fill="#FD3C84" />
      </svg>
    )
  },
  {
    number: "03",
    title: "Creator Value",
    desc: "Creators deserve more control over their business. SugarCribs supports multiple monetization opportunities including subscriptions, premium offerings, direct engagement, custom requests, and tips - within a platform designed for long-term growth.",
    icon: (
      <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.1412 4.54933C12.8675 3.93058 13.2312 3.62058 13.6112 3.43933C14.0449 3.23239 14.5194 3.125 15 3.125C15.4805 3.125 15.955 3.23239 16.3887 3.43933C16.7687 3.62058 17.1325 3.93058 17.8587 4.54933C18.6075 5.18683 19.3675 5.50933 20.3675 5.58808C21.3187 5.66433 21.795 5.70308 22.1925 5.84308C23.11 6.16808 23.8325 6.88933 24.1562 7.80808C24.2962 8.20433 24.335 8.68058 24.4112 9.63308C24.49 10.6331 24.8112 11.3918 25.4487 12.1406C26.0687 12.8668 26.3787 13.2306 26.56 13.6106C26.98 14.4893 26.98 15.5106 26.56 16.3881C26.3787 16.7681 26.0687 17.1318 25.45 17.8581C24.8305 18.5531 24.4644 19.4373 24.4112 20.3668C24.335 21.3181 24.2962 21.7943 24.1562 22.1918C23.9957 22.6449 23.736 23.0564 23.396 23.3961C23.056 23.7359 22.6444 23.9954 22.1912 24.1556C21.795 24.2956 21.3187 24.3343 20.3662 24.4106C19.3662 24.4893 18.6075 24.8106 17.8587 25.4481C17.1325 26.0681 16.7687 26.3781 16.3887 26.5593C15.955 26.7663 15.4805 26.8737 15 26.8737C14.5194 26.8737 14.0449 26.7663 13.6112 26.5593C13.2312 26.3781 12.8675 26.0681 12.1412 25.4493C11.4461 24.8299 10.562 24.4638 9.63246 24.4106C8.68121 24.3343 8.20496 24.2956 7.80746 24.1556C7.35439 23.9951 6.94292 23.7353 6.60315 23.3954C6.26339 23.0554 6.00391 22.6438 5.84371 22.1906C5.70371 21.7943 5.66496 21.3181 5.58871 20.3656C5.53557 19.4367 5.16994 18.553 4.55121 17.8581C3.93121 17.1318 3.62121 16.7681 3.43871 16.3881C3.23196 15.9543 3.12478 15.4797 3.125 14.9992C3.12522 14.5186 3.23282 14.0442 3.43996 13.6106C3.62121 13.2306 3.93121 12.8668 4.54996 12.1406C5.19996 11.3768 5.51121 10.6118 5.58871 9.63183C5.66496 8.68058 5.70371 8.20433 5.84371 7.80683C6.0042 7.35376 6.26394 6.94229 6.60392 6.60253C6.9439 6.26276 7.35553 6.00329 7.80871 5.84308C8.20496 5.70308 8.68121 5.66433 9.63371 5.58808C10.5626 5.53494 11.4463 5.16806 12.1412 4.54933Z" stroke="#FD3C84" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16.5525 13.3871C17.3925 14.0709 17.8125 14.4134 17.8125 14.9996C17.8125 15.5859 17.3925 15.9284 16.5525 16.6121C16.3212 16.8021 16.09 16.9809 15.88 17.1284C15.6802 17.2675 15.4759 17.4 15.2675 17.5259C14.43 18.0346 14.01 18.2896 13.635 18.0071C13.26 17.7246 13.225 17.1359 13.1562 15.9546C13.1371 15.6366 13.1267 15.3182 13.125 14.9996C13.125 14.7059 13.1375 14.3784 13.1562 14.0446C13.225 12.8634 13.2587 12.2734 13.635 11.9921C14.01 11.7096 14.43 11.9646 15.2675 12.4734C15.485 12.6059 15.6938 12.7409 15.88 12.8709C16.0913 13.0184 16.3212 13.1971 16.5525 13.3859" stroke="#FD3C84" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
];

export const AboutUsCorePrinciplesSection = () => {
  const classes = useStyles();

  return (
    <Box className={classes.section}>
      <Typography className={classes.sectionTitle}>
        Our Core Principles
      </Typography>

      <Box className={classes.grid}>
        {PRINCIPLES.map((item, index) => (
          <Box key={index} className={classes.card}>
            <Box className={classes.cardHeader}>
              <Box className={classes.iconBadge}>
                {item.icon}
              </Box>
              <Typography className={classes.cardNumber}>
                {item.number}
              </Typography>
            </Box>
            <Typography className={classes.cardTitle}>
              {item.title}
            </Typography>
            <Typography className={classes.cardDesc}>
              {item.desc}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AboutUsCorePrinciplesSection;
